<script lang="ts">
  import _ from "lodash";
  import type { RetailerType } from "@shared/types/Retailer";
  import type { NotificationPreference } from "@shared/types/UserNotification";
  import { Http } from "@shared/svelte/Modules/ApiService";
  import NotificationPrepare from "@shared/services/notification_response.js";
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";
  import SettingsPageWrapper from "@modern/components/retailer/settings/PageWrapper.svelte";
  import retailerContext from "@modern/services/retailer_context";

  export let notificationsRes;
  let user = window["$rootScope"]?.user;
  let notifications: NotificationPreference[];

  let retailers: { name: string; id: number }[] = _.clone(window["$rootScope"].retailers);
  retailers.unshift({ name: "All Locations", id: null });
  let retailer: RetailerType = retailerContext.get();
  let currentRetailer: RetailerType = _.clone(retailer);
  let notification: { email_preference: boolean; phone_preference: boolean; name: string } = {
    email_preference: false,
    phone_preference: false,
    name: "",
  };
  let buttonText = { email_preference: "Turn All On", phone_preference: "Turn All On" };

  const displayName = function (notification: NotificationPreference) {
    var str = notification.display_name;
    if ((notification.distributor || {}).id) {
      return notification.distributor.name + ": " + str;
    } else {
      return str || "";
    }
  };

  const refresh = function () {
    Http(
      {
        method: "GET",
        url: "/api/retailer/notifications",
        data: {
          retailer_id: currentRetailer.id,
        },
      },
      function (res) {
        retailer = currentRetailer;
        notificationsRes = res;
        prepareNotifications();
        notifications = notifications;
      },
      function (err) {
        console.log(err);
      },
    );
  };

  const decideButtonText = function () {
    if (
      _.every(
        _.filter(notifications, function (p) {
          return p.uses_email;
        }),
        function (x) {
          return x.email_preference;
        },
      )
    ) {
      buttonText.email_preference = "Turn All Off";
      notification.email_preference = true;
    } else {
      buttonText.email_preference = "Turn All On";
      notification.email_preference = false;
    }
    if (
      _.every(
        _.filter(notifications, function (p: NotificationPreference) {
          return p.uses_phone;
        }),
        function (x) {
          return x.phone_preference;
        },
      )
    ) {
      buttonText.phone_preference = "Turn All Off";
      notification.phone_preference = true;
    } else {
      buttonText.phone_preference = "Turn All On";
      notification.phone_preference = false;
    }
  };

  const prepareNotifications = function () {
    const filters = [
      notification => notification.name !== "invite_reminder",
      //temporary - remove invoice due today option until available to term states
      notification => notification.name !== "invoice_due_today",
    ];

    if (!retailer.term_payments) {
      filters.push(notification => notification.name !== "invoice_term_available");
    }

    notificationsRes = notificationsRes.filter(v => filters.every(f => f(v)));
    notifications = NotificationPrepare(notificationsRes, retailer, retailers, user);
    decideButtonText();
  };

  prepareNotifications();

  const toggleAll = function (comType: string) {
    let setting = buttonText[comType] == "Turn All On";
    notifications.forEach(function (x) {
      x[comType] = setting;
      update(x);
    });
    buttonText[comType] = setting ? "Turn All Off" : "Turn All On";
    notifications = notifications;
  };

  const update = function (notification) {
    if (!notification.name) {
      return;
    }
    Http(
      {
        url: "/api/retailer/notifications",
        method: "POST",
        data: {
          notification: {
            retailer_id: retailer.id,
            name: notification.name,
            email_preference: notification.email_preference,
            phone_preference: notification.phone_preference,
          },
        },
      },
      function (res) {
        decideButtonText();
        if (!retailer.id) {
          _.each(["email_preference", "phone_preference"], function (prefName) {
            if (notification[prefName]) {
              notification["someSetHelpText_" + prefName] = false;
            }
          });
          notifications = notifications;
        }
      },
      function (err) {},
    );
  };
</script>

<SettingsPageWrapper>
  <div class="grid-container margin-bottom--small padding-bottom--medium">
    <div class="grid-row">
      <div
        class="grid-col c6-12 vertical-align-middle show--md margin-bottom--medium margin-top--small"
      >
        <div class="default-title--regular">Notifications</div>
      </div>
      <div
        class="grid-col c12-12 c6-12--md vertical-align-middle margin-bottom--medium margin-top--small"
      >
        <div class="input-wrapper select-style">
          <!-- svelte-ignore a11y-no-onchange -->
          <select
            bind:value={currentRetailer.id}
            id="notification_retailer_select"
            on:change={refresh}
          >
            {#each retailers as retailer}
              <option value={retailer.id}>{retailer.name}</option>
            {/each}
          </select>
        </div>
      </div>
    </div>

    <div class="grid-row">
      <div class="grid-col c8-12 vertical-align-middle">
        <div class="default-subheader--bold">Email</div>
        <div class="default-body--regular notranslate padding-bottom--small">
          {user?.email}
        </div>
      </div>
    </div>

    <div class="padding-bottom--smaller align-left gutter--none email_preferences">
      <div class="grid-row settings__notification-row">
        <div
          class="notifications-toggle grid-col c1-12 vertical-align-middle align-right toggle-all-email"
        >
          <input
            type="checkbox"
            bind:checked={notification.email_preference}
            on:change={() => update(notification)}
            class="switch-input float-right"
            name="set-name"
            id="notification-e-{notification.name}"
            on:click={() => toggleAll("email_preference")}
          />
          <label
            for="notification-e-{notification.name}"
            class="switch-label switch-label-animations"
          />
        </div>
        <div class="grid-col c9-12 vertical-align-middle">
          <div class="default-body--bold padding-bottom--smaller">All Email Notifications</div>
        </div>
      </div>
      {#each notifications as notification}
        {#if notification.name != "end_of_day_order_report" && notification.uses_email}
          <div class="grid-row settings__notification-row">
            <div class="notifications-toggle grid-col c1-12 vertical-align-middle align-right">
              <input
                type="checkbox"
                bind:checked={notification.email_preference}
                on:change={() => update(notification)}
                class="switch-input float-right"
                name="set-name"
                id="notification-e-{notification.name}"
              />
              <label
                for="notification-e-{notification.name}"
                class="switch-label switch-label-animations"
              />
            </div>
            <div class="grid-col c9-12 vertical-align-middle">
              <div class="default-body--bold">
                {displayName(notification)}
                {#if notification.someSetHelpText_email_preference}
                  <span class="default-regular--bold">
                    <span class="color-green"
                      >(ON for {notification.someSetHelpText_email_preference}/{retailers.length -
                        1} locations)</span
                    >
                  </span>
                {/if}
              </div>
              <div class="default-body--regular">{notification.description}</div>
            </div>
          </div>
        {/if}
      {/each}
    </div>

    <div class="grid-row margin-top--medium">
      <div class="grid-col c8-12 vertical-align-middle">
        <div class="default-subheader--bold">Cell Phone</div>
        <div class="default-body--regular padding-bottom--small">
          {user.phone
            ? user.phone
            : "Add a mobile phone number to turn on SMS/text notifications"}
        </div>
        {#if !user.phone}
          <UIRouterLink
            stateName={"layout.profile"}
            buttonElement={true}
            button={true}
            classes={"button button--primary"}
          >
            Add Number
          </UIRouterLink>
        {/if}
      </div>
    </div>

    {#if user.phone}
      <div class="padding-bottom--smaller align-left gutter--none phone_preferences">
        <div class="grid-row settings__notification-row">
          <div
            class="notifications-toggle grid-col c1-12 vertical-align-middle align-right toggle-all-phone"
          >
            <input
              type="checkbox"
              bind:checked={notification.phone_preference}
              on:change={() => update(notification)}
              class="switch-input float-right"
              name="set-name"
              id="notification-p-{notification.name}"
              on:click={() => toggleAll("phone_preference")}
            />
            <label
              for="notification-p-{notification.name}"
              class="switch-label switch-label-animations"
            />
          </div>
          <div class="grid-col c9-12 vertical-align-middle">
            <div class="default-body--bold padding-bottom--smaller">All Phone Notifications</div>
          </div>
        </div>
        {#each notifications as notification}
          {#if notification.uses_phone}
            <div class="grid-row settings__notification-row">
              <div class="notifications-toggle grid-col c1-12 align-right vertical-align-middle">
                <input
                  type="checkbox"
                  bind:checked={notification.phone_preference}
                  on:change={() => update(notification)}
                  class="switch-input float-right"
                  name="set-name"
                  id="notification-p-{notification.name}"
                />
                <label
                  for="notification-p-{notification.name}"
                  class="switch-label switch-label-animations"
                />
              </div>
              <div class="grid-col c9-12 vertical-align-middle">
                <div class="default-body--bold">
                  {displayName(notification)}
                  {#if notification.someSetHelpText_phone_preference}
                    <span class="default-regular--bold">
                      <span class="color-green"
                        >(ON for {notification.someSetHelpText_phone_preference}/{retailers.length -
                          1} locations)</span
                      >
                    </span>
                  {/if}
                </div>
                <div class="default-body--regular">{notification.description}</div>
              </div>
            </div>
          {/if}
        {/each}
      </div>
    {/if}
  </div>
</SettingsPageWrapper>
